import React from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import ScoreDonut from "./ScoreDonut";
import { AREA_META, bandColor, shortMonth, focusVaultCard } from "./reportMeta";
import WearableMetrics, {
  BIOMETRIC_WEARABLE_FIELDS, RECOVERY_WEARABLE_FIELDS,
} from "./WearableMetrics";
import { fmtTestTime } from "../bodycomp/bodyCompFields";

const SessionBars = ({ history, max, tint }) => {
  if (!history?.length) {
    return <p className="text-[10px] text-muted-foreground">No sessions yet</p>;
  }
  return (
    <div className="flex items-end gap-2 h-[72px]">
      {history.map((h) => {
        const pct = max ? Math.max(0.08, Math.min(1, (h.score ?? 0) / max)) : 0;
        return (
          <div key={`${h.date}-${h.score}`} className="flex flex-col items-center gap-1">
            <span className="text-[10px] font-medium">{h.score}</span>
            <div
              className="w-6 rounded-t"
              style={{ height: `${pct * 44}px`, background: tint, opacity: 0.55 + pct * 0.45 }}
            />
            <span className="text-[9px] text-muted-foreground">{shortMonth(h.date)}</span>
          </div>
        );
      })}
    </div>
  );
};

export default function DetailedResults({ report }) {
  const navigate = useNavigate();

  // Cocoon Biometric 360 opens the full HOWBODY report and Skin Analysis
  // opens the full MEICET report (Latest / History / Trends); every other area
  // still scrolls to its vault entry card.
  const openArea = (a, meta) => {
    if (a.key === "functional_age") {
      navigate("/portal/longevity/body-composition");
      return;
    }
    if (a.key === "skin_analysis") {
      navigate("/portal/longevity/skin-analysis");
      return;
    }
    if (a.key === "recovery_sleep") {
      navigate("/portal/longevity/recovery-sleep");
      return;
    }
    focusVaultCard(meta.anchor);
  };

  return (
    <section className="space-y-4" data-testid="longevity-detailed-section">
      <div>
        <h2 className="font-editorial text-2xl sm:text-3xl" data-testid="longevity-detailed-heading">
          Your Detailed Results
        </h2>
        <p className="text-sm text-muted-foreground">
          7 diagnostic areas for a longer, healthier, brighter you
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {(report.areas || []).map((a) => {
          const meta = AREA_META[a.key] || {};
          const band = bandColor(a.band);
          return (
            <Card
              key={a.key}
              role="button"
              tabIndex={0}
              onClick={() => openArea(a, meta)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openArea(a, meta);
                }
              }}
              className="rounded-2xl overflow-hidden cursor-pointer select-none outline-none transition-shadow hover:shadow-md focus-visible:ring-2 focus-visible:ring-primary/40"
              data-testid={`longevity-detail-card-${a.key}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-4">
                  <span
                    className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0"
                    style={{ background: `${meta.tint}1f`, color: meta.tint }}
                  >
                    {meta.index}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold uppercase tracking-wide leading-tight">{a.name}</p>
                    <p className="text-[11px] text-muted-foreground leading-snug">{a.subtitle}</p>
                    {(a.source === "howbody" || a.source === "meicet") && (
                      <p
                        className="text-[11px] text-muted-foreground leading-snug mt-0.5"
                        data-testid={`longevity-detail-howbody-${a.key}`}
                      >
                        {a.source === "meicet" ? "MEICET" : "HOWBODY"} · last test {fmtTestTime(a.last_test_time)}
                        {a.report_count > 1 ? ` · ${a.report_count} reports` : ""}
                      </p>
                    )}
                    {a.source === "wearable" && (
                      <p
                        className="text-[11px] text-muted-foreground leading-snug mt-0.5"
                        data-testid={`longevity-detail-wearable-${a.key}`}
                      >
                        {a.provider_label || "Wearable"} · last sync {fmtTestTime(a.last_sync_at)}
                        {a.report_count > 1 ? ` · ${a.report_count} syncs` : ""}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <img
                    src={meta.image}
                    alt={a.name}
                    loading="lazy"
                    className="w-[92px] h-[92px] rounded-xl object-cover flex-shrink-0"
                    data-testid={`longevity-detail-image-${a.key}`}
                  />
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <ScoreDonut
                      value={a.score}
                      max={a.max}
                      size={96}
                      stroke={10}
                      color={band.ring}
                      testId={`longevity-detail-donut-${a.key}`}
                    />
                    <SessionBars history={a.history} max={a.max} tint={meta.tint} />
                  </div>
                </div>

                <div className="flex items-center gap-2 mt-4">
                  <span
                    className="inline-block px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide"
                    style={{ background: band.bg, color: band.fg }}
                    data-testid={`longevity-detail-band-${a.key}`}
                  >
                    {a.band}
                  </span>
                </div>

                {a.key === "recovery_sleep" && (
                  <WearableMetrics
                    metrics={a.wearable_metrics}
                    fields={RECOVERY_WEARABLE_FIELDS}
                    testId="longevity-wearable-recovery"
                  />
                )}
                {a.key === "functional_age" && (
                  <WearableMetrics
                    metrics={a.wearable_metrics}
                    fields={BIOMETRIC_WEARABLE_FIELDS}
                    testId="longevity-wearable-biometric"
                  />
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </section>
  );
}
